import { ScrollView } from "react-native";
import { PressableScale } from "pressto";
import { useRouter, useLocalSearchParams, useNavigation } from "expo-router";
import { useLayoutEffect, useState, useEffect, useMemo } from "react";
import { View, Text, ScreenContainer, Banner, Button } from "@src/components";
import { useHeaderBackButton } from "@src/hooks/useHeaderBackButton";
import { DataStateView } from "@src/components/screen-state";
import { useAlarm, useRoutineSuggestions } from "@src/services/api/hooks";
import type { AlarmRoutine, RoutineSuggestion } from "@src/services/api/types";
import { useUnistyles } from "react-native-unistyles";
import { formatRoutineForDisplay } from "@src/features/alarms/utils/format-routine";
import { useAmplitude } from "@src/hooks/useAmplitude";
import { ANALYTICS_EVENTS } from "@src/services/analytics/events";
import { useTenantId } from "@src/features/tenants";
import { RoutinePreview } from "@src/features/alarms/components/RoutinePreview";
import { Icon } from "@src/components/Icon";
import { AlarmStateBadge } from "@src/features/alarms/components/AlarmStateBadge";
import { AlarmState } from "@src/features/clips";

function routinesAreEqual(routine1: AlarmRoutine | null, routine2: AlarmRoutine | null): boolean {
  if (!routine1 || !routine2) return false;
  if (routine1.kind !== routine2.kind) return false;

  if (routine1.kind === "weekly" && routine2.kind === "weekly") {
    if (routine1.windows.length !== routine2.windows.length) return false;
    // Compare windows (simplified comparison)
    return JSON.stringify(routine1.windows) === JSON.stringify(routine2.windows);
  } else if (routine1.kind === "rotating" && routine2.kind === "rotating") {
    // Compare rotating routines
    return JSON.stringify(routine1) === JSON.stringify(routine2);
  }

  return false;
}

export default function RoutineScreen() {
  const router = useRouter();
  const navigation = useNavigation();
  const { theme } = useUnistyles();
  const { headerLeft } = useHeaderBackButton();
  const { track } = useAmplitude();
  const tenantId = useTenantId();
  const { siteId, cameraKey } = useLocalSearchParams<{
    siteId: string;
    cameraKey: string;
  }>();
  const {
    data: alarm,
    isLoading: isAlarmLoading,
    isFetching: isAlarmFetching,
    error: alarmError,
    refetch: refetchAlarm,
  } = useAlarm(siteId, cameraKey);
  const {
    data: suggestions,
    isLoading: isSuggestionsLoading,
    isFetching: isSuggestionsFetching,
    error: suggestionsError,
    refetch: refetchSuggestions,
  } = useRoutineSuggestions(siteId, cameraKey);

  // Combine states from both queries
  const isLoading = isAlarmLoading || isSuggestionsLoading;
  const isFetching = isAlarmFetching || isSuggestionsFetching;
  const error = alarmError || suggestionsError;
  const refetch = () => {
    refetchAlarm();
    refetchSuggestions();
  };

  // Debug logging
  useEffect(() => {
    console.log("RoutineScreen - cameraKey:", cameraKey);
    console.log("RoutineScreen - suggestions:", suggestions);
    console.log("RoutineScreen - isSuggestionsLoading:", isSuggestionsLoading);
    console.log("RoutineScreen - suggestionsError:", suggestionsError);
  }, [cameraKey, suggestions, isSuggestionsLoading, suggestionsError]);

  const [selectedRoutine, setSelectedRoutine] = useState<AlarmRoutine | null>(null);

  // Initialize selected routine when alarm data loads
  useEffect(() => {
    if (alarm?.settings?.routine) {
      setSelectedRoutine(alarm.settings.routine);
    }
  }, [alarm]);

  // Process suggestions - derive name from time ranges when not provided
  const processedSuggestions: RoutineSuggestion[] = useMemo((): RoutineSuggestion[] => {
    if (!suggestions || !Array.isArray(suggestions)) {
      return [];
    }

    return (suggestions as RoutineSuggestion[]).map((suggestion) => {
      const isDisarmed = suggestion.routine.kind === "weekly" && suggestion.routine.windows.length === 0;
      const action: "armed" | "disarmed" = isDisarmed ? "disarmed" : "armed";

      if (suggestion.name) return { ...suggestion, action };

      const lines = formatRoutineForDisplay(suggestion.routine);
      const timeRange = lines.find((line) => line.timeRanges && line.timeRanges !== "All day")?.timeRanges;
      return { ...suggestion, action, name: timeRange ?? suggestion.name };
    });
  }, [suggestions]);

  // Configure navigation header
  useLayoutEffect(() => {
    navigation.setOptions({
      title: "Change Routine",
      headerBackButtonDisplayMode: "minimal",
      headerStyle: {
        backgroundColor: theme.colors.background,
      },
      headerTintColor: theme.colors.text,
      headerLeft,
    });
  }, [navigation, headerLeft, theme.colors.background, theme.colors.text]);

  // Render routine content
  const renderRoutineContent = (alarmData: NonNullable<typeof alarm>) => {
    const currentRoutine = alarmData.settings?.routine;

    return (
      <ScrollView style={{ flex: 1 }} contentContainerStyle={{ flexGrow: 1, paddingBottom: 40 }}>
        {/* Info Banner */}
        <View px="5xl" pt="4xl" pb="4xl" bg="background">
          <Banner
            variant="info"
            title="What is a routine?"
            message={`Routines set the default schedule for arming and disarming.\nYou can layer exceptions on top for one-off changes.`}
          />
        </View>
        {/* Configured Section */}
        {currentRoutine && (
          <View px="5xl" bg="background">
            <Text textStyle="xs/medium" color="textMuted" mb="lg">
              Current
            </Text>
            <RoutinePreview
              routine={currentRoutine}
              accessoryIcon="chevronRight"
              onPress={() => {
                track(ANALYTICS_EVENTS.ALARM_ROUTINE_EDITED, {
                  camera_id: cameraKey,
                  tenant_id: tenantId,
                  site_id: siteId,
                });
                // Navigate to customize-routine with the current routine as initial data
                const routineJson = encodeURIComponent(JSON.stringify(currentRoutine));
                router.push(
                  `/tenants/${tenantId}/sites/${siteId}/cameras/${cameraKey}/alarms/customize-routine?routine=${routineJson}`,
                );
              }}
            />
          </View>
        )}

        {/* Suggested Section */}
        <View px="5xl" mt="7xl" bg="background" flex={1}>
          <Text textStyle="xs/medium" color="textMuted" mb="lg">
            Common routines
          </Text>

          {processedSuggestions.map((suggestion, index) => (
            <PressableScale
              key={index}
              onPress={() => {
                track(ANALYTICS_EVENTS.ALARM_ROUTINE_EDITED, {
                  camera_id: cameraKey,
                  tenant_id: tenantId,
                  site_id: siteId,
                  source: "suggestion",
                });
                // Navigate to customize-routine with the suggestion routine as initial data
                const routineJson = encodeURIComponent(JSON.stringify(suggestion.routine));
                router.push(
                  `/tenants/${tenantId}/sites/${siteId}/cameras/${cameraKey}/alarms/customize-routine?routine=${routineJson}`,
                );
              }}
              style={{ marginBottom: theme.spacing.lg }}
            >
              <View
                bg="surface"
                p="4xl"
                rounded={8}
                borderWidth={1}
                borderColor={routinesAreEqual(selectedRoutine, suggestion.routine) ? "navy" : "border"}
              >
                <View flexDirection="row" justifyContent="space-between">
                  <View flex={1}>
                    <View flexDirection="row" alignItems="center" gap="md" mb="2xl">
                      {suggestion.name && (
                        <Text textStyle="sm/medium" color="text">
                          {suggestion.name}
                        </Text>
                      )}
                      <AlarmStateBadge state={suggestion.action as "armed" | "disarmed"} />
                    </View>

                    {formatRoutineForDisplay(suggestion.routine).map((line, lineIndex) => (
                      <View key={lineIndex} flexDirection="row" alignItems="center" gap="sm" mb="md">
                        <Icon name="calendarOutline" color={theme.colors.textMuted} size={16} />
                        <Text textStyle="xs/regular" color="textMuted">
                          {line.name}
                        </Text>
                        {line.timeRanges && (
                          <>
                            <Text textStyle="xs/regular" color="textMuted">
                              •
                            </Text>
                            <Icon name="clockOutline" color={theme.colors.textMuted} size={16} />
                            <Text textStyle="xs/regular" color="textMuted">
                              {line.timeRanges}
                            </Text>
                          </>
                        )}
                      </View>
                    ))}
                  </View>
                  <Icon
                    name="chevronRight"
                    color={theme.colors.textMuted}
                    style={{ marginLeft: theme.spacing["3xl"] }}
                  />
                </View>
              </View>
            </PressableScale>
          ))}
        </View>
      </ScrollView>
    );
  };

  return (
    <ScreenContainer edges={[]}>
      <DataStateView
        data={alarm}
        isLoading={isLoading}
        isFetching={isFetching}
        error={error}
        refetch={refetch}
        loadingMessage="Loading routine settings..."
        errorTitle="Alarm not found"
        errorMessage="Could not load alarm routine settings."
      >
        {renderRoutineContent}
      </DataStateView>
    </ScreenContainer>
  );
}
